import { type Configuration, LogLevel } from '@azure/msal-browser';

export const getScopes = () => [`${import.meta.env.VITE_AZURE_CLIENT_ID}/.default`];

/**
 * @see https://learn.microsoft.com/en-us/entra/msal/javascript/browser/v4-migration#2-update-your-msal-configuration
 */
export const msalConfig: Configuration = {
  auth: {
    clientId: import.meta.env.VITE_AZURE_CLIENT_ID!,
    authority: `https://login.microsoftonline.com/${import.meta.env.VITE_AZURE_TENANT_ID}`,
    // MSAL v5 requires a dedicated redirect bridge page for all auth flows (login,
    // silent iframe, popup). The bridge calls broadcastResponseToMainFrame() and uses
    // BroadcastChannel API to communicate back to the main window — needed for COOP
    // support (Entra ID enables COOP by default). Using a lightweight page prevents
    // the full SPA from loading inside hidden iframes or popups.
    // Register this URI in the Entra ID app registration:
    //   - dev  → http://localhost:5173/redirect.html
    //   - prod → https://<domain>/redirect.html
    redirectUri: `${window.location.origin}/redirect.html`,
    // After logout, land on the SPA root (not the redirect bridge page).
    postLogoutRedirectUri: window.location.origin,
    // navigateToLoginRequestUrl was removed in MSAL browser v5 — MsalProvider
    // calls handleRedirectPromise() inline during initialisation and the app
    // renders directly at the redirectUri without any intermediate navigation.
    // Post-login routing to /chat is handled by InnerApp's useEffect (main.tsx)
    // and the AuthLayout loading guard (_auth.tsx).
  },
  cache: {
    // v5 default is sessionStorage. We use localStorage so tokens survive
    // page refreshes and are shared across tabs (required for cross-tab logout).
    cacheLocation: 'localStorage',
    // storeAuthStateInCookie was removed in v5: replaced by the redirect bridge
    // mechanism. Iframes still exist as a fallback (acquireTokenSilent step 5),
    // but they use BroadcastChannel API instead of cookies to pass auth state.
  },
  system: {
    // Blocks interactive redirects (loginRedirect/acquireTokenRedirect) inside iframes.
    // Does NOT affect acquireTokenSilent's internal iframe fallback (step 5) — that
    // uses a separate code path (acquireTokenByIframe) not gated by this setting.
    allowRedirectInIframe: false,
    // v5 renamed iframeHashTimeout → iframeBridgeTimeout, windowHashTimeout → popupBridgeTimeout.
    // These control how long to wait for the redirect bridge response via BroadcastChannel.
    // Default is 6s; 10s gives headroom on slow networks.
    iframeBridgeTimeout: 10000,
    popupBridgeTimeout: 10000,
    loggerOptions: {
      loggerCallback: (level, message, containsPii) => {
        if (containsPii) {
          return;
        }
        switch (level) {
          case LogLevel.Error:
            console.error(message);
            return;
          case LogLevel.Warning:
            console.warn(message);
            return;
          case LogLevel.Info:
            console.info(message);
            return;
          case LogLevel.Verbose:
            console.debug(message);
            return;
          default:
            return;
        }
      },
    },
  },
};
